import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import * as topojson from "topojson-client";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

function CrnaKarta({ topoData, censusData }) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const wrapDim = useResizeObserver(wrapperRef);

  const darkMode = false;

  useEffect(() => {
    if (!wrapDim || !censusData) return;
    const format = d3.format(",");

    //append tooltip
    const tooltip = d3.select(".tooltip");

    // const color = d3
    //   .scaleSequential()
    //   .domain([-0.3, 0.3])
    //   .interpolator(d3.interpolatePiYG);

    const color = d3
      .scaleLinear()
      .domain([-0.45, 0, 0.2])
      .range([-1, 0, 1])
      .interpolate((a, b) =>
        a < 0
          ? (t) => d3.interpolateReds(1 - t)
          : (t) => d3.interpolateGreens(t)
      );

    const svg = d3.select(svgRef.current);

    const zoom = d3.zoom().scaleExtent([1, 8]).on("zoom", zoomed);

    svg.selectAll("*").remove();

    let height = window.innerHeight;
    let width = window.innerWidth;

    let mobile = false;
    if (height > width) mobile = true;

    // console.log(data);

    const opcine = topojson.feature(topoData, topoData.objects["JLS"]);
    const projection = d3
      .geoAlbers()
      .rotate([-15, 0])
      .fitExtent(
        [
          [0, 20],
          [width, height],
        ],
        opcine
      );

    const path = d3.geoPath(projection);

    const titleLocation = projection([11.5, 46.4]);

    svg.attr("viewBox", [0, 0, width, height]);

    const overlay = svg
      .append("g")
      .attr("transform", `translate(${titleLocation[0]},${titleLocation[1]})`);

    const titleGroup = overlay.append("g");
    if (!mobile) {
      const anchor = titleGroup.append("text").classed("title", true);
      anchor.append("tspan").text("Promjena stanovništva po općinama");
      // anchor.append("tspan").attr("dy", 40).attr("x", 0).text("po općinama");

      titleGroup
        .append("text")
        .attr("dy", 30)
        .classed("subtitle", true)
        .text("Usporedba cenzusa 2021. / 2011.");

      titleGroup
        .append("text")
        .attr("dy", 80)
        .classed("subtitle-credits", true)
        .text("Autor: Velimir Gašparović (@velimirgasp)")
        .on("click", () => {
          window.open("https://twitter.com/velimirgasp", "_blank");
        });

      titleGroup
        .append("text")
        .attr("dy", 105)
        .classed("subtitle-credits", true)
        .text("Podaci: Državni zavod za statistiku (@StatistikaHR)")
        .on("click", () => {
          window.open("https://twitter.com/StatistikaHR", "_blank");
        });
    }

    const content = svg.append("g");

    const legendColors = d3.range(-0.3, 0.2, 0.1);
    const legendLocation = projection([18.6, 44.55]);
    const legendGroup = content
      .append("g")
      .attr(
        "transform",
        `translate(${legendLocation[0]},${legendLocation[1]})`
      );

    legendGroup
      .append("text")
      .text("Promjena")
      .classed("legend-title", true)
      .attr("dy", -10)
      .attr("dx", 16);
    legendColors.forEach((value, index) => {
      const legendEntry = legendGroup
        .append("g")
        .attr("transform", `translate(0, ${index * 32})`);
      legendEntry
        .append("rect")
        .attr("width", 16)
        .attr("height", 16)
        .attr("stroke", "black")
        .attr("stroke-width", 0.5)
        .attr("fill", color(value));

      legendEntry
        .append("text")
        .attr("class", "legend-label")
        .attr("dx", -5)
        .attr("dy", 14)
        .text(value > -0.05 && value < 0.05 ? "0%" : d3.format("+.0%")(value));
    });

    const mapGroup = content.append("g");

    const opcineGroup = mapGroup
      .selectAll("path")
      .data(opcine.features)
      .join("path")
      .classed("granice", true)
      // .attr("stroke", "black")
      // .attr("stroke-width", 0.5)
      // .attr("stroke-linejoin", "round")
      .attr("fill", (d) => {
        const topoName = d.properties.JLS_IME;

        const filtered = censusData.filter(
          (d) => d.municipality.toUpperCase() === topoName.toUpperCase()
        );

        if (filtered.length === 0) return "purple";

        return color(filtered[0].populationChange);
      })
      .attr("d", path);

    opcineGroup.on("mousemove", function (event, d) {
      const topoName = d.properties.JLS_IME;

      tooltip.select(".tooltip-title").html(`${topoName}`);
      tooltip.style("visibility", "visible");

      const tooltipWidth = 250;
      var yOffset = event.y + 50;
      if (yOffset > height / 2) yOffset = yOffset - 300;

      tooltip.style("top", `${yOffset}px`);

      var xOffset = event.x;

      if (xOffset > width / 2) xOffset -= tooltipWidth + 50;

      if (!mobile) tooltip.style("left", `${xOffset}px`);
      else tooltip.style("left", `${(width - tooltipWidth) / 2}px`);

      const filtered = censusData.filter(
        (d) => d.municipality.toUpperCase() === topoName.toUpperCase()
      );

      if (filtered.length === 0) return topoName;
      const item = filtered[0];

      tooltip
        .select(".tooltip-value-previous")
        .html(format(item.previousPopulation));
      tooltip
        .select(".tooltip-value-current")
        .html(format(item.currentPopulation));
      tooltip
        .select(".tooltip-value-change")
        .html(d3.format(".2%")(item.populationChange));
    });

    opcineGroup.on("mouseout", (event, d) => {
      tooltip.style("visibility", "hidden");
    });

    svg.call(zoom);

    function zoomed(event) {
      const { transform } = event;
      tooltip.style("visibility", "hidden");
      content.attr("transform", transform);

      if (transform.k > 1) {
        // legendGroup.transition().style("opacity", 0);
        titleGroup.transition().style("opacity", 0);
      } else {
        // legendGroup.transition().style("opacity", 1);
        titleGroup.transition().style("opacity", 1);
      }
    }
  }, [wrapDim]);

  return (
    <div>
      <div ref={wrapperRef}>
        <div>
          <svg ref={svgRef}></svg>
        </div>
      </div>
      <div className="tooltip">
        <div className="tooltip-title">Title</div>
        <div className="tooltip-content">
          <div className="tooltip-row">
            <div>Broj stanovnika 2011.</div>
            <div className="tooltip-value tooltip-value-previous">22,000</div>
          </div>
          <div className="tooltip-row">
            <div>Broj stanovnika 2021.</div>
            <div className="tooltip-value tooltip-value-current">21,000</div>
          </div>
          <div className="tooltip-row">
            <div>Promjena</div>
            <div className="tooltip-value-change">-3.55%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CrnaKarta;
